/**
 * Golden-path declarations (UI/UX plan §2, ruling D4) — the SINGLE SOURCE for the
 * end-to-end user workflow `Workshops → Kompass → Suche → Bewerbungen`.
 *
 * Every workflow surface declares, HERE, the next step(s) it advances to. Three
 * consumers read this ONE table so they can never drift apart:
 *   1. the surfaces themselves — each renders its declared step through the shared
 *      `nextStep` primitive (`app/lib/next-step.ts`), resolving the label via `t()`
 *      and computing its own locked/unlocked state;
 *   2. the walkthrough inventory — derives the storyboard's primary arrow graph from
 *      the `to` targets (no more scanning source for route-shaped literals);
 *   3. `__tests__/nav-integrity.test.ts` — asserts the SPINE chain from `/workshops`
 *      reaches `/applications` with no gaps, every target resolves to a real route,
 *      and no workflow surface lacks a declaration.
 *
 * The `spine` edge is the pipeline-advancing one the completeness walk follows (each
 * surface has at most one). Non-spine edges are branch forwards (e.g. the Kompass
 * resources feeding into the search) — declared so their arrows draw, but off the
 * critical chain. `/applications` is the declared TERMINAL (an empty entry): the
 * pipeline board is the end of the golden path, not a gap.
 */

import type { CareerKey } from "../i18n"
import type { NextStep } from "./domain"

/** One declared forward step from a workflow surface. */
export interface NextStepDecl {
  /** The route this step advances to (English code, AX-9). */
  readonly to: string
  /** The i18n key for the German forward label. */
  readonly labelKey: CareerKey
  /** True = the pipeline-advancing SPINE edge (the completeness walk follows it). */
  readonly spine?: boolean
}

/** The golden path, keyed by surface tag. `[]` = a declared TERMINAL (end of the path). */
export const GOLDEN_PATH: Readonly<Record<string, readonly NextStepDecl[]>> = {
  // ── Workshops → Kompass (the W1 »Deine Werte« track is the spine's opening leg) ──
  "career-workshop-list": [{ to: "/workshops/values", labelKey: "next.toCardgame", spine: true }],
  "career-values-cardgame": [{ to: "/workshops/values/interview", labelKey: "next.toInterview", spine: true }],
  "career-values-interview": [{ to: "/workshops/values/sektion", labelKey: "next.toSektion", spine: true }],
  // W1 »Wer ich bin« hands off to the CV-Werkbank BEFORE W2 (operator direction 2026-07-28):
  // the sooner an existing CV + reference material land, the more they steer the rest of the
  // pipeline. Providing material is on the spine (recommended) but skippable — a person with no
  // CV still proceeds, since the Werkbank's forward is unconditional. From there the spine
  // rejoins W2 »Was ich suche« → W3, so the user is still walked THROUGH the gate `/search`
  // enforces (W4 ruling D3) rather than around it into a locked door.
  "career-werte-sektion": [{ to: "/compass/cv", labelKey: "next.provideMaterial", spine: true }],
  // The CV-Werkbank (provide → ingest → resolve) is a SPINE node here: its onward step is W2.
  // It carries only this spine edge (no branch), so the golden walk has one canonical forward.
  "career-cv-workbench": [{ to: "/workshops/search-profile", labelKey: "next.toSearchProfile", spine: true }],
  "career-search-profile": [{ to: "/workshops/profiler", labelKey: "next.toDecisionProfile", spine: true }],
  // W4 »Schreibstil« is a BRANCH off W1, not a spine node: it hangs off the Werte-Sektion
  // (its own server prerequisite) rather than sitting between two pipeline steps, and its
  // product is the writing-style ASSET the Kompass holds. So it declares ONE non-spine
  // forward into the Kompass; the spine chain is untouched.
  "career-writing-style": [{ to: "/compass", labelKey: "next.toKompass" }],
  "career-decision-profile": [{ to: "/compass", labelKey: "next.toKompass", spine: true }],
  // ── Kompass → Suche (S567: the DOCUMENT at `/compass` is the spine node — the hub that used
  // to hold this edge is deleted; the Kompass reference views are branch forwards into it) ──
  "career-profile": [{ to: "/search", labelKey: "next.toSuche", spine: true }],
  "career-werte-view": [{ to: "/compass", labelKey: "next.toKompass" }],
  // ── Suche → Bewerbungen (the terminal) ──
  "career-search": [{ to: "/applications", labelKey: "next.toBewerbungen", spine: true }],
  "career-applications": [], // TERMINAL — the pipeline board ends the golden path.
}

/**
 * Surfaces intentionally OUTSIDE the golden path — Kompass reference libraries, not
 * pipeline steps. A curated exemption list (like {@link KNOWN_STALE}) so a NEW pipeline
 * surface added without a declaration trips the completeness test instead of slipping
 * through. Detail (`/:id`), print, dev and redirect surfaces are excluded structurally,
 * not here.
 */
export const NON_WORKFLOW_SURFACES: readonly string[] = [
  "career-evidence-ledger", // Belege — the claim ledger (a library view)
  "career-assets", // Nachweise — the document inventory (a library view)
]

/** The SPINE step a surface advances along the pipeline (its one `spine` edge), if any. */
export function spineDecl(surface: string): NextStepDecl | undefined {
  return (GOLDEN_PATH[surface] ?? []).find((d) => d.spine)
}

/** The primary forward step a surface renders — its spine edge, else its first branch edge. */
export function forwardDecl(surface: string): NextStepDecl | undefined {
  const decls = GOLDEN_PATH[surface] ?? []
  return decls.find((d) => d.spine) ?? decls[0]
}

/** Resolve a declaration into a render-ready {@link NextStep} — label via `t`, optional lock. */
export function toNextStep(decl: NextStepDecl, label: string, lockedReason?: string): NextStep {
  return { url: decl.to, label, ...(lockedReason ? { lockedReason } : {}) }
}

/** The declared terminal of the golden path — the completeness walk must land here. */
export const GOLDEN_TERMINAL = "/applications"

/** The declared entry of the golden path — where the completeness walk starts. */
export const GOLDEN_START = "/workshops"
